/** Reporting engine — widget renderers.
 *
 * Each widget renders BODY ONLY; the card chrome (title bar, edit handles) is the
 * Board's WidgetFrame. Everything derives from a (filtered, month-scoped) ticket
 * array so drill-down stays coherent with the headline numbers. Ported from the
 * dashboard mockup; styling is inline (the design's own language), scoped under
 * .rp-root.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";

import type { ReportingTicketRow } from "../services/api";

import {
  A,
  C,
  CSAT_TIER_COLOR,
  CSAT_TIER_LABELS,
  CSAT_TIER_ORDER,
  DIMENSIONS,
  LEADERBOARDS,
  METRICS,
  MONO,
  PRIORITY_ORDER,
  SANS,
  applyFilters,
  cohortForWidget,
  csatTierCounts,
  fmtMetric,
  isRated,
  opened,
  rated,
  tierOf,
  unitMetric,
} from "./data";
import { SortableTable } from "./tableSort";
import type { Column } from "./tableSort";
import type { BoardKey, DimensionKey, Drill, MetricKey, Widget } from "./types";

type Row = ReportingTicketRow;

const f1 = (n: number): string => (Math.round(n * 10) / 10).toFixed(1);
const pct = (n: number): number => Math.round(n * 100);

/** Measure an element in real CSS pixels so charts can be drawn 1:1.
 *
 * Charts used to be drawn into a fixed viewBox and stretched to the widget with
 * preserveAspectRatio="none", which scales x and y by different factors as soon
 * as the widget is resized away from that viewBox's aspect ratio — smearing the
 * axis text, the hairline gridlines and the stroke weights. Drawing at the
 * measured size keeps every mark crisp at any widget size.
 *
 * The ref is a callback ref so it re-observes if the node is swapped out, and it
 * measures during commit (before paint) so there is no first-frame flash.
 */
function useBoxSize<T extends HTMLElement>() {
  const [size, setSize] = useState({ w: 0, h: 0 });
  const obs = useRef<ResizeObserver | null>(null);
  const ref = useCallback((node: T | null) => {
    obs.current?.disconnect();
    obs.current = null;
    if (!node || typeof ResizeObserver === "undefined") return;
    const measure = (w: number, h: number): void =>
      setSize((s) => (Math.abs(s.w - w) < 0.5 && Math.abs(s.h - h) < 0.5 ? s : { w, h }));
    measure(node.clientWidth, node.clientHeight);
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (r) measure(r.width, r.height);
    });
    ro.observe(node);
    obs.current = ro;
  }, []);
  useEffect(() => () => obs.current?.disconnect(), []);
  return [ref, size] as const;
}

export function PriorityPill({
  priority,
  small,
}: {
  priority: string;
  small?: boolean;
}) {
  const map: Record<string, { bg: string; fg: string }> = {
    Critical: { bg: C.orange, fg: "#fff" },
    High: { bg: C.purple, fg: "#fff" },
    Medium: { bg: C.forest, fg: C.green },
    Low: { bg: "#ece8de", fg: C.ink },
  };
  const s = map[priority] ?? map.Medium;
  return (
    <span
      style={{
        display: "inline-block",
        padding: small ? "1px 7px" : "2px 10px",
        fontSize: small ? 9 : 10,
        fontFamily: MONO,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        background: s.bg,
        color: s.fg,
        whiteSpace: "nowrap",
      }}
    >
      {priority}
    </span>
  );
}

function StatusDot({ status }: { status: string }) {
  // Open tickets read in the deep green, Closed in iQuest purple. C.forest (not the
  // neon C.green) is the green used for every mark on a light surface across the
  // reporting module; the bright green only ever sits on a dark background.
  const c = status === "Closed" ? C.purple : C.forest;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, color: C.mud }}>
      <i style={{ width: 7, height: 7, borderRadius: "50%", background: c, display: "inline-block" }} />
      {status}
    </span>
  );
}

function Delta({
  metricKey,
  cur,
  prev,
}: {
  metricKey: MetricKey;
  cur: number | null;
  prev: number | null;
}) {
  const m = METRICS[metricKey];
  // Either side missing means there is no comparison to make. A month with no
  // evidence has not "improved" or "declined" against one that had some.
  if (cur == null || prev == null || m.better === "flat") return null;
  const diff = cur - prev;
  const base: CSSProperties = { fontSize: 10.5, fontFamily: MONO, letterSpacing: "0.02em" };
  if (Math.abs(diff) < (m.kind === "count" ? 0.5 : 0.005)) {
    return <span style={{ ...base, color: C.stone }}>no change vs prev</span>;
  }
  const up = diff > 0;
  const good = (m.better === "up" && up) || (m.better === "down" && !up);
  let show: string;
  if (m.kind === "pct") {
    show = `${Math.abs(pct(cur) - pct(prev))}pt`;
  } else if (m.kind === "count") {
    show = `${Math.abs(Math.round(diff))}`;
  } else if (m.kind === "min") {
    // Duration change: minutes under an hour of movement, else hours.
    const abs = Math.abs(diff);
    show = abs >= 60 ? `${f1(abs / 60)}h` : `${Math.round(abs)}m`;
  } else {
    show = `${f1(Math.abs(diff))}h`;
  }
  return (
    <span style={{ ...base, color: good ? C.forest : C.orange }}>
      {up ? "▲" : "▼"} {show} vs prev
    </span>
  );
}

/** Height reserved above the plot for the opened/closed key. */
const LINE_LEGEND_H = 16;

/** The trend chart's key.
 *
 * The two series were only ever told apart by the card's subtitle
 * ("Trend · opened vs closed"), which the widget header no longer carries
 * (issue #89) — so the chart names them itself. */
function LineLegend() {
  const item = (label: string, stroke: string, dashed: boolean) => (
    <span key={label} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
      <svg width="16" height="6" aria-hidden="true" style={{ display: "block" }}>
        <line
          x1="0"
          x2="16"
          y1="3"
          y2="3"
          stroke={stroke}
          strokeWidth={dashed ? 1.4 : 1.8}
          strokeDasharray={dashed ? "3 2" : undefined}
        />
      </svg>
      {label}
    </span>
  );
  return (
    <div
      style={{
        height: LINE_LEGEND_H,
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: 14,
        fontFamily: MONO,
        fontSize: 9.5,
        letterSpacing: "0.04em",
        color: C.stone,
      }}
    >
      {item("opened", C.forest, false)}
      {item("closed", C.ink, true)}
    </div>
  );
}

function LineChart({ tickets, days, compact }: { tickets: Row[]; days: number; compact?: boolean }) {
  const data = A.daily(tickets, days);
  const max = Math.max(2, ...data.map((d) => Math.max(d.o, d.c))) + 1;
  const [box, size] = useBoxSize<HTMLDivElement>();
  // Drawn at the widget's own pixel size (see useBoxSize); the constants are only
  // the pre-measurement fallback for the very first render. The legend sits above
  // the plot, so the chart itself gets what is left of the measured box.
  const W = Math.max(120, Math.floor(size.w) || 760);
  const H = Math.max(60, (Math.floor(size.h) || (compact ? 150 : 230)) - LINE_LEGEND_H);
  const pad = { l: 26, r: 10, t: 14, b: 22 };
  const iw = W - pad.l - pad.r;
  const ih = H - pad.t - pad.b;
  const x = (i: number): number => pad.l + (i / Math.max(1, data.length - 1)) * iw;
  const y = (v: number): number => pad.t + ih - (v / max) * ih;
  const line = (k: "o" | "c"): string =>
    data.map((d, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(d[k]).toFixed(1)}`).join(" ");
  const area =
    `M${x(0)},${pad.t + ih} ` +
    data.map((d, i) => `L${x(i).toFixed(1)},${y(d.o).toFixed(1)}`).join(" ") +
    ` L${x(data.length - 1)},${pad.t + ih} Z`;
  const ticks: number[] = [];
  const step = Math.ceil(max / 4);
  for (let v = 0; v <= max; v++) if (v % step === 0) ticks.push(v);
  // Thin the day labels to what actually fits, so a narrow widget doesn't collide
  // them into each other.
  const labelStep = Math.max(1, Math.ceil(days / Math.max(2, Math.floor(iw / 58))));
  const dayLabels: number[] = [];
  for (let d = 1; d <= days; d += labelStep) dayLabels.push(d);
  if (days - dayLabels[dayLabels.length - 1] >= labelStep * 0.6) dayLabels.push(days);
  return (
    <div ref={box} style={{ width: "100%", height: "100%" }}>
      <LineLegend />
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: "block" }}>
        <defs>
          <linearGradient id="rpWArea" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={C.green} stopOpacity="0.32" />
            <stop offset="100%" stopColor={C.green} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {ticks.map((v) => {
          // Half-pixel so a 1px gridline lands on one device row, not across two.
          const gy = Math.round(y(v)) + 0.5;
          return (
            <g key={v}>
              <line x1={pad.l} x2={W - pad.r} y1={gy} y2={gy} stroke="#ece8de" strokeWidth="1" />
              <text x={pad.l - 6} y={gy + 3} textAnchor="end" fontSize="9" fill={C.stone} fontFamily={MONO}>
                {v}
              </text>
            </g>
          );
        })}
        <path d={area} fill="url(#rpWArea)" />
        <path d={line("o")} fill="none" stroke={C.forest} strokeWidth="1.8" />
        <path d={line("c")} fill="none" stroke={C.ink} strokeWidth="1.4" strokeDasharray="3 2" />
        {dayLabels.map((d) => (
          <text key={d} x={x(d - 1)} y={H - 5} textAnchor="middle" fontSize="9" fill={C.stone} fontFamily={MONO}>
            {d}
          </text>
        ))}
      </svg>
    </div>
  );
}

function Ring({ metricKey, tickets, big }: { metricKey: MetricKey; tickets: Row[]; big?: boolean }) {
  const m = METRICS[metricKey];
  const raw = m.compute(tickets);
  // No evidence draws an empty track and reads "n/a": a gauge showing a full
  // arc off nothing measured is the worst thing this widget can do (issue #97).
  const hasValue = raw != null;
  let frac = 0;
  if (raw != null) {
    if (m.kind === "pct") frac = raw;
    else if (m.target)
      frac = m.better === "down" ? Math.min(1, m.target / Math.max(raw, 0.01)) : Math.min(1, raw / m.target);
    else frac = Math.min(1, raw / 50);
    frac = Math.max(0, Math.min(1, frac));
  }
  const [box, size] = useBoxSize<HTMLDivElement>();
  // The gauge tracks its widget instead of snapping between two hard-coded sizes,
  // and every dimension below is a fixed ratio of `sz` — so it scales uniformly,
  // and drawing at whole pixels keeps the arc and the number crisp.
  const fallback = big ? 132 : 84;
  // Nothing sits under the gauge anymore, so the whole card height is the arc's
  // to use — it gets most of the card's width and height instead of leaving a
  // strip for a caption.
  const room = Math.min(
    (Math.floor(size.h) || fallback) * 0.94,
    (Math.floor(size.w) || 260) * 0.8,
  );
  const sz = Math.max(72, Math.min(148, Math.floor(room) || fallback));
  const r = sz * 0.41;
  const sw = Math.max(6, Math.min(11, sz * 0.075));
  const cx = sz / 2;
  const circ = 2 * Math.PI * r;
  const dash = circ * frac;
  const good = m.better === "up" ? frac >= 0.9 : m.better === "down" ? frac >= 0.85 : true;
  const arc = good ? C.forest : C.orange;
  // Just the value, centred in the card. The metric's own name used to print to
  // the right of the number (a duplicate of the card title, taken off the KPI
  // tiles in issue #89 and the gauges in #118); the "target …" caption that sat
  // under the number has been dropped too, so the gauge is now only its arc and
  // its number. The arc still fills against the metric's reference value, that
  // reference is just no longer spelled out on the card.
  return (
    <div
      ref={box}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        height: "100%",
      }}
    >
      <svg width={sz} height={sz} viewBox={`0 0 ${sz} ${sz}`} style={{ flex: "0 0 auto", overflow: "visible" }}>
        <circle cx={cx} cy={cx} r={r} fill="none" stroke="#ece8de" strokeWidth={sw} />
        {hasValue && (
          <circle
            cx={cx}
            cy={cx}
            r={r}
            fill="none"
            stroke={arc}
            strokeWidth={sw}
            strokeDasharray={`${dash} ${circ}`}
            strokeLinecap="round"
            transform={`rotate(-90 ${cx} ${cx})`}
          />
        )}
        <text
          x={cx}
          y={cx}
          textAnchor="middle"
          dominantBaseline="central"
          fontFamily={SANS}
          fontWeight="600"
          fontSize={Math.round(sz * 0.226)}
          fill={hasValue ? C.ink : C.stone}
        >
          {fmtMetric(metricKey, raw)}
          {hasValue && m.kind === "pct" ? "%" : ""}
        </text>
      </svg>
    </div>
  );
}

function CsatBreakdown({
  tickets,
  big,
  onDrill,
}: {
  tickets: Row[];
  big?: boolean;
  onDrill?: (d: Drill) => void;
}) {
  // WidgetBody hands over the filtered MONTH feed (applyFilters uses the cohort
  // only for day filtering), so narrow it here the way A.* does. rated() is the
  // same cohort the drill-down lists, so the counts and the popout agree.
  const { counts, total, unclassified } = csatTierCounts(rated(tickets));
  const positive = counts.gold + counts.green;
  const classified = total - unclassified;
  const pct = classified ? Math.round((positive / classified) * 100) : null;
  if (!total) {
    return (
      <div style={{ fontSize: 12, color: C.stone, display: "flex", alignItems: "center", height: "100%" }}>
        No satisfaction ratings returned this period.
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", justifyContent: "center" }}>
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {CSAT_TIER_ORDER.map((tier) => {
          const n = counts[tier];
          return (
            <DrillRow
              key={tier}
              style={{
                display: "grid",
                gridTemplateColumns: "10px minmax(0,1fr) 30px",
                alignItems: "center",
                gap: 9,
                padding: big ? `6px 0 6px ${DRILL_ROW_GUTTER}px` : `4px 0 4px ${DRILL_ROW_GUTTER}px`,
                borderBottom: `1px dotted ${C.rule}`,
                // A zero tier stays visible but recedes: "no Review ratings" is
                // the useful part of a clean month, so it must not be hidden.
                opacity: n ? 1 : 0.45,
              }}
              // An empty tier is not drillable: it would open a popout with
              // nothing in it, having offered a click that leads nowhere.
              drill={
                n
                  ? {
                      label: `Rated ${CSAT_TIER_LABELS[tier]}`,
                      match: (t) => tierOf(t) === tier,
                    }
                  : null
              }
              onDrill={onDrill}
            >
              <span style={{ width: 10, height: 10, borderRadius: 2, background: CSAT_TIER_COLOR[tier] }} />
              <span style={{ fontSize: big ? 13 : 12, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {CSAT_TIER_LABELS[tier]}
              </span>
              <span style={{ fontFamily: MONO, fontSize: big ? 13 : 12, textAlign: "right", color: CSAT_TIER_COLOR[tier], fontWeight: 600 }}>
                {n}
              </span>
            </DrillRow>
          );
        })}
        {unclassified > 0 && (
          <DrillRow
            style={{ display: "grid", gridTemplateColumns: "10px minmax(0,1fr) 30px", alignItems: "center", gap: 9, padding: `4px 0 4px ${DRILL_ROW_GUTTER}px` }}
            // Rated, but carrying a temperature we could not map to a tier.
            drill={{ label: "Rating not recognised", match: (t) => isRated(t) && !tierOf(t) }}
            onDrill={onDrill}
          >
            <span style={{ width: 10, height: 10, borderRadius: 2, background: C.stone }} />
            <span style={{ fontSize: 12, color: C.ink }}>Other</span>
            <span style={{ fontFamily: MONO, fontSize: 12, textAlign: "right", color: C.stone }}>{unclassified}</span>
          </DrillRow>
        )}
      </ul>
      {/* The score is always shown, next to the number of ratings it came from.
          It used to be withheld below five ratings as "too few to score", but a
          client averaging three responses a month never cleared that bar, so the
          headline satisfaction figure was permanently suppressed while the KPI
          tile beside it printed a percentage anyway (issue #93). The sample size
          is the honest caveat, and it is right there. */}
      <div style={{ fontSize: 10.5, color: C.stone, fontFamily: MONO, marginTop: 8 }}>
        {total} rating{total === 1 ? "" : "s"}
        {pct != null ? ` · ${pct}% positive` : ""}
      </div>
    </div>
  );
}

function KpiTile({
  metricKey,
  tickets,
  prevTickets,
}: {
  metricKey: MetricKey;
  tickets: Row[];
  prevTickets: Row[] | null;
}) {
  const m = METRICS[metricKey];
  const cur = m.compute(tickets);
  const prev = prevTickets ? m.compute(prevTickets) : null;
  const unit = unitMetric(metricKey, cur);
  // Centred on both axes to match the gauge tiles (Answered on time / Open
  // backlog) sitting beside these. The number is the whole tile now: the trailing
  // sparkline came off with the top-left layout, since a squiggle pinned to the
  // bottom edge pulled the eye straight back off-centre.
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        height: "100%",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
        {/* "n/a" in the muted tone, never a fabricated 0. A month with no
            ratings has no satisfaction score, and printing 0% off nobody's
            opinion is a worse claim than saying so (issue #97). */}
        <span
          style={{
            fontFamily: SANS,
            fontWeight: 600,
            fontSize: cur == null ? 32 : 48,
            lineHeight: 1,
            letterSpacing: "-0.025em",
            color: cur == null ? C.stone : C.ink,
          }}
        >
          {fmtMetric(metricKey, cur)}
        </span>
        {/* Only render the unit when there is one, so a plain count stays exactly
            centred rather than being nudged left by an empty span + gap. */}
        {unit && <span style={{ fontSize: 16, color: C.stone }}>{unit}</span>}
      </div>
      {/* Period-over-period change when there's a prior month to compare against;
          Delta returns null otherwise, so the number stays perfectly centred with
          nothing beneath it. The metric's own name never sits here — it would just
          duplicate the card title above (issue #89). */}
      <Delta metricKey={metricKey} cur={cur} prev={prev} />
    </div>
  );
}

// Left inset applied to every drillable row (BarList, Leaderboard, CsatBreakdown)
// so the hover/focus accent (an inset box-shadow at the row's left edge, see
// .rp-drill-row in reporting.css) gets a gutter instead of hugging the label. It
// lives in each row's inline padding rather than a CSS rule because the inline
// shorthand padding would otherwise override an external .rp-drill-row rule. The
// recent-tickets table is a plain <table> with no DrillRow, so it is unaffected.
const DRILL_ROW_GUTTER = 10;

const barStyles = {
  list: { listStyle: "none", padding: 0, margin: 0 } as CSSProperties,
  row: {
    display: "grid",
    gridTemplateColumns: "minmax(0,1.3fr) minmax(50px,1fr) 30px",
    alignItems: "center",
    gap: 10,
    padding: `5px 0 5px ${DRILL_ROW_GUTTER}px`,
    borderBottom: `1px dotted ${C.rule}`,
    fontSize: 12,
  } as CSSProperties,
  // A drillable row moves its rule onto the <li> and its layout onto the button
  // inside, so the whole row is the click target rather than just the text in it,
  // and the hover tint covers the row instead of hugging the label.
  listItemReset: { listStyle: "none", padding: 0, margin: 0 } as CSSProperties,
  rowButton: {
    font: "inherit",
    color: "inherit",
    background: "none",
    border: "none",
    width: "100%",
    textAlign: "left",
    cursor: "pointer",
  } as CSSProperties,
  // Pin the family and size on the label itself, not just the row. When a bar row
  // is drillable its layout moves onto a <button> whose `font: inherit` (rowButton)
  // wipes the row's fontSize, so a label that leaned on the row cascade would
  // balloon to the inherited base size. Setting it here keeps every bar label at
  // Figtree 12 — matching the leaderboard names, which pin their own size the same way.
  name: { fontFamily: SANS, fontSize: 12, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } as CSSProperties,
  track: { background: "#ece8de", height: 6, overflow: "hidden", borderRadius: 1 } as CSSProperties,
  fill: { display: "block", height: "100%" } as CSSProperties,
  count: { fontFamily: MONO, fontSize: 11, textAlign: "right", color: C.forest } as CSSProperties,
};

/** A breakdown row, clickable when the widget can drill down (issue #117).
 *
 * Renders as a plain row when it cannot: a display-only widget, or one whose
 * drill-down is switched off, must not offer an affordance that does nothing. The
 * click stops here, because the board wraps every widget in a card that opens the
 * UNFILTERED drill-down when clicked — which is still what clicking the card's
 * chrome does, so both readings of "open this widget" stay available. */
function DrillRow({
  style,
  drill,
  onDrill,
  children,
}: {
  style: CSSProperties;
  drill: Drill | null;
  onDrill?: (d: Drill) => void;
  children: ReactNode;
}) {
  if (!drill || !onDrill) return <li style={style}>{children}</li>;
  return (
    <li style={{ ...barStyles.listItemReset, borderBottom: style.borderBottom }}>
      <button
        type="button"
        className="rp-drill-row"
        title={`Show the tickets behind ${drill.label}`}
        onClick={(e) => {
          e.stopPropagation();
          onDrill(drill);
        }}
        style={{ ...style, ...barStyles.rowButton, borderBottom: "none" }}
      >
        {children}
      </button>
    </li>
  );
}

function BarList({
  dimension,
  tickets,
  onDrill,
}: {
  dimension: DimensionKey;
  tickets: Row[];
  onDrill?: (d: Drill) => void;
}) {
  const d = DIMENSIONS[dimension];
  const rows = A.rows(tickets, d.key);
  const max = Math.max(1, ...rows.map((r) => r.count));
  if (!rows.length) return <Empty />;
  return (
    <ul style={barStyles.list}>
      {rows.map((r) => {
        // Every breakdown bar renders in the one deep green. The per-priority
        // palette (High purple / Low grey) and the other dimensions' accents
        // (sub-category purple, source mud) were dropped in favour of a single
        // uniform colour across the breakdowns. The dimension's colorBy/accent
        // fields are left in data.ts but no longer drive the bar fill.
        const color = C.forest;
        return (
          <DrillRow
            key={r.name}
            style={barStyles.row}
            // A.rows counts the opened cohort, which is exactly the cohort the
            // drill-down lists for a bar widget, so the row's number IS the
            // number of tickets the popout shows.
            drill={{ label: `${d.rowLabel} · ${r.name}`, match: (t) => t[d.key] === r.name }}
            onDrill={onDrill}
          >
            <span style={barStyles.name}>{r.name}</span>
            <span style={barStyles.track}>
              <span style={{ ...barStyles.fill, width: `${(r.count / max) * 100}%`, background: color }} />
            </span>
            <span style={barStyles.count}>{r.count}</span>
          </DrillRow>
        );
      })}
    </ul>
  );
}

function Leaderboard({
  board,
  tickets,
  onDrill,
}: {
  board: BoardKey;
  tickets: Row[];
  onDrill?: (d: Drill) => void;
}) {
  const b = LEADERBOARDS[board];
  // Top five. A.leaderboard has already dropped anyone who does not belong in a
  // client-facing panel, so this is five real people.
  const rows = A.leaderboard(tickets, b.key).slice(0, 5);
  const max = Math.max(1, ...rows.map((r) => r.tickets));
  if (!rows.length) return <Empty />;
  return (
    <ol style={{ listStyle: "none", padding: 0, margin: 0 }}>
      {rows.map((r, i) => (
        <DrillRow
          key={r.name}
          style={{
            display: "grid",
            gridTemplateColumns: "18px 1fr 28px",
            alignItems: "center",
            gap: 10,
            padding: `6px 0 6px ${DRILL_ROW_GUTTER}px`,
            borderBottom: `1px dotted ${C.rule}`,
          }}
          // The requester board counts human-raised tickets only, while the
          // drill-down lists the whole opened cohort — so in principle an
          // auto-generated ticket carrying this person's name would show in the
          // popout without being in the row's count. In practice the alert queues
          // have no requester at all (they land on "Unspecified", which is never a
          // board row), so the two agree.
          drill={{ label: `${b.rowLabel} · ${r.name}`, match: (t) => t[b.key] === r.name }}
          onDrill={onDrill}
        >
          <span style={{ fontFamily: MONO, fontSize: 12, color: C.stone }}>{i + 1}</span>
          <span style={{ minWidth: 0 }}>
            <span style={{ fontSize: 12, color: C.ink, display: "block", marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {r.name}
            </span>
            <span style={barStyles.track}>
              <span style={{ ...barStyles.fill, width: `${(r.tickets / max) * 100}%`, background: b.accent }} />
            </span>
          </span>
          <span style={barStyles.count}>{r.tickets}</span>
        </DrillRow>
      ))}
    </ol>
  );
}

function Empty() {
  return (
    <div style={{ fontFamily: MONO, fontSize: 11, color: C.stone, padding: "16px 0" }}>
      No tickets match these filters.
    </div>
  );
}

const tableStyles = {
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12 } as CSSProperties,
  th: {
    textAlign: "left",
    padding: "7px 8px",
    borderBottom: `2px solid ${C.ink}`,
    fontWeight: 500,
    fontSize: 9.5,
    textTransform: "uppercase",
    letterSpacing: "0.09em",
    color: C.stone,
    fontFamily: MONO,
    position: "sticky",
    top: 0,
    background: "#fff",
    zIndex: 1,
  } as CSSProperties,
  td: { padding: "8px 8px", borderBottom: `1px dotted ${C.rule}`, color: C.ink } as CSSProperties,
};

/** Newest first by opened time, and opened-cohort only. "Recent tickets" is a
 * register of the tickets RAISED this month, so a ticket merely completed this
 * month (opened earlier) does not belong in it. The feed arrives in cache order,
 * so without the sort the tile's 8-row slice would be an arbitrary 8 rather than
 * the latest 8. */
function recentFirst(tickets: Row[]): Row[] {
  return opened(tickets)
    .slice()
    .sort((a, b) => b.openedISO.localeCompare(a.openedISO));
}

/** Severity rank for sorting, so one click on Priority puts Critical on top
 *  rather than ordering the labels alphabetically. Shared with the drill-down's
 *  table via the Column definitions in each. */
export function priorityRank(t: Row): number {
  const i = (PRIORITY_ORDER as readonly string[]).indexOf(t.priority);
  return i === -1 ? PRIORITY_ORDER.length : i;
}

/** The register's columns. Sorting is click-to-sort per column (issue #121);
 *  until a header is clicked the order is recentFirst's, newest at the top. */
const REGISTER_COLUMNS: Column<Row>[] = [
  {
    key: "id",
    label: "ID",
    value: (t) => t.ticketNumber ?? (t.id != null ? String(t.id) : null),
    cell: (t) => t.ticketNumber ?? t.id ?? "",
    cellStyle: { fontFamily: MONO, color: C.purple, whiteSpace: "nowrap" },
  },
  { key: "subject", label: "Subject", value: (t) => t.subject, cell: (t) => t.subject },
  {
    key: "contact",
    label: "Contact",
    value: (t) => t.contact,
    cell: (t) => t.contact,
    cellStyle: { color: C.mud, whiteSpace: "nowrap" },
  },
  {
    key: "priority",
    label: "Priority",
    value: priorityRank,
    cell: (t) => <PriorityPill priority={t.priority} small />,
  },
  {
    key: "status",
    label: "Status",
    value: (t) => t.status,
    cell: (t) => <StatusDot status={t.status} />,
  },
  {
    key: "response",
    label: "Response",
    right: true,
    value: (t) => t.firstResponseMins,
    cell: (t) => (t.firstResponseMins != null ? `${t.firstResponseMins}m` : "—"),
    cellStyle: { fontFamily: MONO, whiteSpace: "nowrap" },
  },
  {
    key: "engineer",
    label: "Staff member",
    value: (t) => t.engineer,
    cell: (t) => t.engineer,
    cellStyle: { whiteSpace: "nowrap" },
  },
];

export function TicketTable({ tickets, limit }: { tickets: Row[]; limit?: number | null }) {
  const ordered = recentFirst(tickets);
  // Sorting reorders the rows the tile SHOWS, so the cut is taken first: a tile
  // titled "Recent tickets" keeps showing the most recent ones however they are
  // sorted, rather than quietly becoming "the eight slowest this month". The
  // whole cohort is sortable in the drill-down, which lists all of it.
  const rows = limit ? ordered.slice(0, limit) : ordered;
  return (
    <div style={{ overflow: "auto", height: "100%" }}>
      <SortableTable
        rows={rows}
        columns={REGISTER_COLUMNS}
        rowKey={(t, i) => t.ticketNumber ?? String(t.id ?? `row-${i}`)}
        tableStyle={tableStyles.table}
        thStyle={tableStyles.th}
        tdStyle={tableStyles.td}
        emptyMessage="No tickets match these filters."
      />
    </div>
  );
}

export function WidgetBody({
  widget,
  tickets,
  prevTickets,
  days,
  big,
  onDrill,
}: {
  widget: Widget;
  tickets: Row[];
  prevTickets?: Row[] | null;
  days: number;
  big?: boolean;
  /** Present only when this widget's drill-down is available. A row inside a
   *  breakdown calls it to open the popout narrowed to that row (issue #117). */
  onDrill?: (d: Drill) => void;
}) {
  // Day filters are read against the cohort the widget measures, so a pinned day
  // range on "Tickets completed" narrows by the day work FINISHED rather than by
  // the day the ticket happened to be raised (which for that cohort may be in an
  // earlier month entirely).
  const cohort = cohortForWidget(widget);
  const ft = applyFilters(tickets, widget.filters, cohort);
  const fp = prevTickets ? applyFilters(prevTickets, widget.filters, cohort) : null;
  switch (widget.type) {
    case "kpi":
      return widget.metric ? <KpiTile metricKey={widget.metric} tickets={ft} prevTickets={fp} /> : null;
    case "ring":
      return widget.metric ? <Ring metricKey={widget.metric} tickets={ft} big={big} /> : null;
    case "line":
      return <LineChart tickets={ft} days={days} compact={!big} />;
    case "bar":
      return widget.dimension ? (
        <BarList dimension={widget.dimension} tickets={ft} onDrill={onDrill} />
      ) : null;
    case "leaderboard":
      return widget.board ? (
        <Leaderboard board={widget.board} tickets={ft} onDrill={onDrill} />
      ) : null;
    case "csat":
      return <CsatBreakdown tickets={ft} big={big} onDrill={onDrill} />;
    case "table":
      return <TicketTable tickets={ft} limit={big ? null : 8} />;
    default:
      return null;
  }
}
